import AdminRoute from "../../auth/RestrictedRoute";
import { ROLES } from "../../../config/constants";
import { DownloadIcon, CopyIcon } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { useQuery } from "react-query";
import { apiConfig } from "../../../config/apiConfig";
import { useCookie } from "../../../hooks/useCookie";
import apiService from "../../../service/apiService";
import { useAuthContext } from "../../../context/auth";
import { useParams } from "react-router-dom";
import { useToast } from "../../../hooks/useToast";

// TODO: add venez logo at the center of the qr code

type VisiteType = "place" | "offer";

function generateQrCodeData(
  visiteType: VisiteType,
  partner: PlaceManager,
  options: {
    offerId?: string;
    offerSlug?: string;
    placeCategorySlug?: string;
    placeSlug?: string;
  },
): {
  qrCodeData: string;
  qrDownloadName: string;
} {
  const webBaseUrl =
    process.env?.REACT_APP_WEB_URL ?? "https://staging.venez.ma";
  if (visiteType === "offer") {
    const qrCodeData = `${webBaseUrl}/visite/${partner.partnerPlaceSlug}/offres/${options.offerSlug}`;
    const qrDownloadName = `qrCode-place-${options.placeSlug}-offer-${options.offerSlug}`;
    return {
      qrCodeData,
      qrDownloadName,
    };
  } else {
    const qrCodeData = `${webBaseUrl}/visite/${partner.partnerPlaceSlug}/places/${options.placeCategorySlug}/${options.placeSlug}`;
    const qrDownloadName = `qrCode-place-${options.placeSlug}`;
    return {
      qrCodeData,
      qrDownloadName,
    };
  }
}

export function QRCodesPage({ visiteType }: { visiteType: VisiteType }) {
  const { user } = useAuthContext();
  const params = useParams();
  const { token } = useCookie("vToken");
  const endpoint = apiConfig.users.placeManagers.list({ noPagination: "true" });
  const { data } = useQuery<any, any, PaginatedResponse<PlaceManager>, any>(
    ["getMultiPlaceAdminPartners"],
    () => {
      return apiService.MakeGetRequest(endpoint, token);
    },
    { keepPreviousData: false },
  );
  const { data: offerData } = useQuery<any, any, SpecialOffers, any>(
    ["getMultiPlaceAdminOffer"],
    () => {
      return apiService.MakeGetRequest(
        `specials/ours/${params.offerId}`,
        token,
      );
    },
    {
      enabled: visiteType === "offer",
      keepPreviousData: false,
    },
  );

  return (
    <AdminRoute route={ROLES.PLACE_ADMIN} requireMultiPlaceAdmin={true}>
      <h5 className="fs-4 fw-bold mb-2">Codes QR</h5>
      {visiteType === "offer" ? (
        <h5 className="fs-4 fw-bold mb-2 mt-3">Offre: {offerData?.title}</h5>
      ) : (
        ""
      )}
      <div className="mt-4 d-flex flex-row gap-5 flex-wrap">
        {data?.items?.map((partner) => {
          const { qrCodeData, qrDownloadName } = generateQrCodeData(
            visiteType,
            partner,
            {
              offerId: params.offerId,
              offerSlug: offerData?.slug,
              placeCategorySlug: user?.adminForPlace?.category.slug,
              placeSlug: user?.adminForPlace?.slug,
            },
          );
          return (
            <QRCodeItem
              name={partner.fullName}
              qrData={qrCodeData}
              qrCodeName={qrDownloadName}
            />
          );
        })}
      </div>
    </AdminRoute>
  );
}

function QRCodeItem({
  name: qrCodeName,
  qrCodeName: qrDownloadName,
  qrData,
}: {
  name: string;
  qrCodeName: string;
  qrData: string;
}) {
  const { toastSuccess } = useToast();

  const handleDownload = () => {
    const canvas = document.querySelector(
      `#qr-${qrCodeName.replace(/\s+/g, "-")}`,
    ) as HTMLCanvasElement;

    if (canvas) {
      const highResCanvas = document.createElement("canvas");
      const size = 1000;
      const padding = 80;

      highResCanvas.width = size;
      highResCanvas.height = size;
      const ctx = highResCanvas.getContext("2d");

      if (ctx) {
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, size, size);

        const qrSize = size - padding * 2;

        ctx.drawImage(canvas, padding, padding, qrSize, qrSize);

        highResCanvas.toBlob(
          (blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.download = `${qrDownloadName}.png`;
              link.href = url;
              link.click();
              URL.revokeObjectURL(url);
            }
          },
          "image/png",
          1.0,
        );
      }
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(qrData).then(() => {
      toastSuccess("Lien copié!");
    });
  };

  const qrId = `qr-${qrCodeName.replace(/\s+/g, "-")}`;

  return (
    <div className="d-flex flex-col align-items-center bg-white shadow-sm p-3 rounded-sm">
      {/* partner name */}
      <div>{qrCodeName}</div>
      {/* qr code */}
      <div
        className="mt-3"
        style={{
          width: "300px",
          height: "300px",
          backgroundColor: "white",
        }}
      >
        <QRCodeCanvas
          id={qrId}
          size={1000}
          level="H"
          value={qrData}
          style={{
            height: "300px",
            width: "300px",
          }}
        />
      </div>
      {/* copy and download buttons */}
      <div className="d-flex align-items-center justify-content-center gap-2 w-full">
        <button
          type="button"
          onClick={handleCopy}
          className="flex mt-4 outline-none border-0 btn btn-secondary rounded-pill text-white d-flex align-items-center gap-2 justify-content-center"
        >
          <CopyIcon /> Copier
        </button>
        <button
          type="button"
          onClick={handleDownload}
          className="flex mt-4 outline-none border-0 btn btn-primary rounded-pill text-white d-flex align-items-center gap-2 justify-content-center"
        >
          <DownloadIcon /> Télécharger
        </button>
      </div>
    </div>
  );
}
